import { get } from 'lodash';

import {
  getWalkthroughForProjectType,
  isOnboardingStageDone,
  ONBOARDING_STAGE,
} from 'src/data/onboarding';
import { CAMPAIGN_STATUS } from 'src/data/campaignStatus';
import {
  isProjectTypeInAdsCategory,
  isProjectTypeInSocialCategory,
  PROJECT_STATUS,
  PROJECT_TYPE,
  isBareboneProject,
  isDocumentProject,
} from 'src/data/project';
import { contentTypeFromBriefType } from 'src/data/brief';
import { CONTENT_TYPE } from 'src/data/contentType';
import { isOnboardingTesting } from './testingUtils';
import { AVAILABLE_ONBOARDING_STAGES } from './const';

/**
 * Utils
 */

const isProductAdsProjectType = (projectType, briefType) =>
  isProjectTypeInAdsCategory(projectType) &&
  contentTypeFromBriefType(briefType) === CONTENT_TYPE.product;

export const projectTypeSupportsOnboardingStage = (projectType, briefType, stage) => {
  // Check full walkthrough support
  const walkthroughStage = getWalkthroughForProjectType(projectType);
  if (stage === walkthroughStage) {
    return true;
  }

  // Check additional stages
  switch (stage) {
    case ONBOARDING_STAGE.briefEditing:
      // todo add ff
      if (projectType === PROJECT_TYPE.facebookAd) return false;
      return (
        isProductAdsProjectType(projectType, briefType) ||
        isProjectTypeInSocialCategory(projectType) ||
        [
          PROJECT_TYPE.seoMetaDescription,
          PROJECT_TYPE.blogPost,
          PROJECT_TYPE.emailSubjectLine,
          PROJECT_TYPE.emailPromotional,
          PROJECT_TYPE.emailColdOutreach,
          PROJECT_TYPE.sms,
        ].includes(projectType)
      );

    case ONBOARDING_STAGE.briefEditingContinuousOptimization:
      return projectType === PROJECT_TYPE.continuousOptimization;

    default:
      return false;
  }
};

export const finishedOnboarding = (user, stage) => {
  // TESTING
  if (isOnboardingTesting(stage)) {
    return false;
  }
  return isOnboardingStageDone(user.onboardingStages, stage);
};

export const onboardingStagePending = (stage, user, project) => {
  const projectType = get(project, 'type');
  const briefType = get(project, 'brief.type');
  const result =
    !isDocumentProject(projectType) &&
    projectTypeSupportsOnboardingStage(projectType, briefType, stage) &&
    !finishedOnboarding(user, stage);

  return result;
};

const getPendingStages = (currentUser, currentProject) =>
  AVAILABLE_ONBOARDING_STAGES.filter((stage) =>
    onboardingStagePending(stage, currentUser, currentProject)
  );

export const getActiveOnboardingStage = (currentUser, currentProject) => {
  const pendingStages = getPendingStages(currentUser, currentProject);
  return get(pendingStages, 0);
};

export const commonProjectReady = (project) => {
  // Project is not ready if there was no generation requests
  const firstRequest = get(project, 'generationRequests.edges[0].node', []);
  if (!firstRequest) {
    return false;
  }

  // Generation results should also have some variations
  return firstRequest.variations && firstRequest.variations.length > 0;
};

export const blogBuilderProjectReady = (project) =>
  get(project, 'status') === PROJECT_STATUS.blogEditing;

export const landingCoProjectReady = (project) => {
  const assets = get(project, 'landingPageAssets', []);
  const campaign = get(project, 'continuousOptimizationCampaign') || {};
  return assets.length > 0 && campaign.status === CAMPAIGN_STATUS.draft;
};

export const isProjectReadyForOnboardingStage = (stage, project) => {
  if (!stage) {
    return false;
  }
  switch (stage) {
    // "common" and "pd" project should have generation results
    case ONBOARDING_STAGE.commonProjectWalkthrough:
    case ONBOARDING_STAGE.productDescriptionWalkthrough:
      return commonProjectReady(project);

    // "CO" project should have assets and inactive campaign
    case ONBOARDING_STAGE.continuousOptimizationWalkthrough:
      return landingCoProjectReady(project);

    // project should be in "editing" stage
    case ONBOARDING_STAGE.briefEditing:
    case ONBOARDING_STAGE.briefEditingContinuousOptimization:
      return !isBareboneProject(project);

    default:
      return true;
  }
};
